import * as THREE from 'three';
import HeroModule from '$src/js/modules/hero/HeroModule';

const DEG2RAD = Math.PI / 180;
const MAX_ROTATION_ANGLE = 25 * DEG2RAD;

const getHeroHeight = (): number => {
  if (window.innerWidth >= 640) {
    const measureHeightElement = document.createElement('div');
    measureHeightElement.style.cssText = 'position:fixed;height:100svh;pointer-events:none;visibility:hidden';
    document.documentElement.appendChild(measureHeightElement);
    const height = measureHeightElement.offsetHeight;
    document.documentElement.removeChild(measureHeightElement);
    return height;
  }
  const vh = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--vh'));
  return vh * 100;
};

export class SceneService {
  private renderer: THREE.WebGLRenderer | null = null;
  private scene: THREE.Scene | null = null;
  private camera: THREE.PerspectiveCamera | null = null;
  private displayGroup: THREE.Group | null = null;
  private dirty = true;
  private disposed = false;
  private animationFrameId: number | null = null;
  private ambientLight: THREE.AmbientLight | null = null;
  private keyLight: THREE.DirectionalLight | null = null;
  private fillLight: THREE.DirectionalLight | null = null;
  private rimLight: THREE.DirectionalLight | null = null;

  /**
   * Initializes the scene.
   * @param canvas The canvas element.
   */
  readonly initialize = (canvas: HTMLCanvasElement): void => {
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      alpha: true,
      antialias: true,
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(window.innerWidth, getHeroHeight());
    this.renderer.toneMapping = THREE.CineonToneMapping;
    this.renderer.toneMappingExposure = 1.2;
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    this.scene = new THREE.Scene();

    this.camera = new THREE.PerspectiveCamera(
      40,
      window.innerWidth / getHeroHeight(),
      0.1,
      100,
    );
    const startZ = HeroModule.getInstance().intro.getCameraStartZ();
    this.camera.position.set(0, 0, startZ);
    this.camera.lookAt(0, 0.5, 0);

    this.displayGroup = new THREE.Group();
    this.scene.add(this.displayGroup);

    this.setupLighting();

    window.addEventListener('resize', this.handleResize);
    this.startRenderLoop();

    void HeroModule.getInstance().model.loadAssets();
  };

  /**
   * Sets up the lighting for the scene.
   */
  private readonly setupLighting = (): void => {
    if (!this.scene) return;

    // this.ambientLight = new THREE.AmbientLight(0xffffff, 0);
    // this.scene.add(this.ambientLight);

    // this.keyLight = new THREE.DirectionalLight(0xffffff, 0);
    // this.keyLight.position.set(3, 5, 4);
    // this.scene.add(this.keyLight);

    this.fillLight = new THREE.DirectionalLight(0xCE9804, 20);
    this.fillLight.position.set(-6, 1, -2);
    this.scene.add(this.fillLight);

    // this.rimLight = new THREE.DirectionalLight(0xfff0e0, 0);
    // this.rimLight.position.set(0, 2, -4);
    // this.scene.add(this.rimLight);
  };

  /**
   * Starts the render loop.
   */
  private readonly startRenderLoop = (): void => {
    const render = (): void => {
      if (this.disposed) {
        return;
      }

      this.animationFrameId = requestAnimationFrame(render);

      HeroModule.getInstance().texture.updateCrossfade();

      if (this.dirty) {
        this.renderer?.render(this.scene!, this.camera!);
        this.dirty = false;
      }
    };

    render();
  };

  /**
   * Handles screen resize events.
   */
  private readonly handleResize = (): void => {
    const w = window.innerWidth;
    const h = getHeroHeight();

    if (this.camera) {
      this.camera.aspect = w / h;
      this.camera.updateProjectionMatrix();
    }

    this.renderer?.setSize(w, h);

    // Update model scale and position based on orientation
    HeroModule.getInstance().model.updateModelTransform();

    this.dirty = true;
  };

  /**
   * Sets the rotation of the display group.
   */
  readonly setRotation = (normalizedValue: number): void => {
    if (!this.displayGroup) {
      return;
    }

    this.displayGroup.rotation.y = normalizedValue * MAX_ROTATION_ANGLE;
    this.dirty = true;
  };

  /**
   * Marks the scene as dirty for rendering.
   */
  readonly markDirty = (): void => {
    this.dirty = true;
  };

  /**
   * Gets the scene.
   */
  readonly getScene = (): THREE.Scene | null => this.scene;

  /**
   * Gets the camera.
   */
  readonly getCamera = (): THREE.PerspectiveCamera | null => this.camera;

  /**
   * Gets the display group.
   */
  readonly getDisplayGroup = (): THREE.Group | null => this.displayGroup;

  /**
   * Gets the maximum anisotropy supported by the renderer.
   */
  readonly getMaxAnisotropy = (): number => this.renderer?.capabilities.getMaxAnisotropy() ?? 1;

  /**
   * Gets the ambient light.
   */
  readonly getAmbientLight = (): THREE.AmbientLight | null => this.ambientLight;

  /**
   * Gets the key light.
   */
  readonly getKeyLight = (): THREE.DirectionalLight | null => this.keyLight;

  /**
   * Gets the fill light.
   */
  readonly getFillLight = (): THREE.DirectionalLight | null => this.fillLight;

  /**
   * Gets the rim light.
   */
  readonly getRimLight = (): THREE.DirectionalLight | null => this.rimLight;

  /**
   * Disposes of the scene service.
   */
  readonly dispose = (): void => {
    this.disposed = true;

    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
    }

    window.removeEventListener('resize', this.handleResize);

    this.scene?.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        const mesh = obj as THREE.Mesh;
        mesh.geometry.dispose();

        const mat = mesh.material;

        if (Array.isArray(mat)) {
          mat.forEach((m) => m.dispose());
        } else {
          mat.dispose();
        }
      }
    });

    this.renderer?.dispose();
  };
}
